import type { BarProcessingSetting, GridValue, MidiNote, MidiTimeSignature, TrackRole } from "./music";

export type PersistedProjectTrack = {
  id: number;
  name: string;
  role: TrackRole;
  notes: MidiNote[];
  enabled: boolean;
};

export type PersistedProject = {
  schemaVersion: 1;
  id: "current";
  title: string;
  exportMemo: string;
  exportCounter: number;
  lastModified: string;
  notes: MidiNote[];
  tracks: PersistedProjectTrack[];
  timing: { ppq: number; timeSignature: MidiTimeSignature };
  settings: {
    bpm: number;
    grid: GridValue;
    timeNudgeGrid: GridValue;
    minimumNoteBeats: number;
    lengthAssist: boolean;
    micSensitivity: number;
    noteDetail: number;
    referenceBars: number;
    barSettings: Record<number, BarProcessingSetting>;
  };
};

export type ProjectStorageKind = "indexeddb" | "localstorage";

const DATABASE_NAME = "p010-hum-midi-projects";
const DATABASE_VERSION = 1;
const STORE_NAME = "projects";
const CURRENT_PROJECT_ID = "current";
const FALLBACK_KEY = "p010.current-project.v1";
const GRIDS = new Set<GridValue>(["1/4", "1/8", "1/16", "1/32"]);
const ROLES = new Set<TrackRole>(["melody", "bass", "strings", "synth"]);

function finite(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(minimum, Math.min(maximum, value))
    : fallback;
}

function validNotes(value: unknown): MidiNote[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!candidate || typeof candidate !== "object") return [];
    const note = candidate as Partial<MidiNote>;
    if (typeof note.midi !== "number" || typeof note.startBeat !== "number" || typeof note.durationBeats !== "number") return [];
    if (![note.midi, note.startBeat, note.durationBeats].every(Number.isFinite)) return [];
    return [{
      ...note,
      midi: Math.max(0, Math.min(127, note.midi)),
      startBeat: note.startBeat,
      durationBeats: Math.max(1 / 960, note.durationBeats),
      velocity: finite(note.velocity, 92, 1, 127),
    } as MidiNote];
  });
}

export function normalizePersistedProject(value: unknown): PersistedProject | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<PersistedProject>;
  if (candidate.schemaVersion !== 1 || candidate.id !== CURRENT_PROJECT_ID) return null;
  const settings = candidate.settings ?? {} as PersistedProject["settings"];
  const timing = candidate.timing ?? { ppq: 480, timeSignature: { numerator: 4, denominator: 4 } };
  const timeSignature = timing.timeSignature ?? { numerator: 4, denominator: 4 };
  const tracks = Array.isArray(candidate.tracks) ? candidate.tracks.flatMap((track) => {
    if (!track || typeof track !== "object") return [];
    const item = track as Partial<PersistedProjectTrack>;
    const role = ROLES.has(item.role as TrackRole) ? item.role as TrackRole : "melody";
    return [{
      id: Math.max(1, Math.round(finite(item.id, 1, 1, Number.MAX_SAFE_INTEGER))),
      name: typeof item.name === "string" ? item.name.slice(0, 48) : "Track",
      role,
      notes: validNotes(item.notes),
      enabled: item.enabled !== false,
    }];
  }) : [];
  return {
    schemaVersion: 1,
    id: CURRENT_PROJECT_ID,
    title: typeof candidate.title === "string" && candidate.title.trim() ? candidate.title.slice(0, 64) : "無題の曲",
    exportMemo: typeof candidate.exportMemo === "string" ? candidate.exportMemo.slice(0, 32) : "",
    exportCounter: Math.max(1, Math.round(finite(candidate.exportCounter, 1, 1, 9999))),
    lastModified: typeof candidate.lastModified === "string" ? candidate.lastModified : new Date(0).toISOString(),
    notes: validNotes(candidate.notes),
    tracks,
    timing: {
      ppq: Math.round(finite(timing.ppq, 480, 24, 9600)),
      timeSignature: {
        numerator: Math.round(finite(timeSignature.numerator, 4, 1, 32)),
        denominator: Math.round(finite(timeSignature.denominator, 4, 1, 128)),
      },
    },
    settings: {
      bpm: finite(settings.bpm, 100, 20, 400),
      grid: GRIDS.has(settings.grid) ? settings.grid : "1/8",
      timeNudgeGrid: GRIDS.has(settings.timeNudgeGrid) ? settings.timeNudgeGrid : "1/8",
      minimumNoteBeats: finite(settings.minimumNoteBeats, 0.25, 1 / 32, 8),
      lengthAssist: settings.lengthAssist !== false,
      micSensitivity: finite(settings.micSensitivity, 65, 0, 100),
      noteDetail: finite(settings.noteDetail, 72, 0, 100),
      referenceBars: Math.round(finite(settings.referenceBars, 4, 1, 64)),
      barSettings: settings.barSettings && typeof settings.barSettings === "object" ? settings.barSettings : {},
    },
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDBを開けませんでした"));
  });
}

async function loadFromIndexedDb(): Promise<PersistedProject | null> {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(CURRENT_PROJECT_ID);
      request.onsuccess = () => resolve(normalizePersistedProject(request.result));
      request.onerror = () => reject(request.error ?? new Error("Projectを読み込めませんでした"));
    });
  } finally {
    database.close();
  }
}

async function saveToIndexedDb(project: PersistedProject): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(project);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Projectを保存できませんでした"));
      transaction.onabort = () => reject(transaction.error ?? new Error("Project保存が中断されました"));
    });
  } finally {
    database.close();
  }
}

export async function loadCurrentProject(): Promise<{ project: PersistedProject | null; storage: ProjectStorageKind }> {
  if (typeof window === "undefined") return { project: null, storage: "localstorage" };
  if ("indexedDB" in window) {
    try {
      return { project: await loadFromIndexedDb(), storage: "indexeddb" };
    } catch (caught) {
      console.warn("[P-010 project] IndexedDB load failed; using localStorage fallback", caught);
    }
  }
  const stored = window.localStorage.getItem(FALLBACK_KEY);
  try {
    return { project: stored ? normalizePersistedProject(JSON.parse(stored)) : null, storage: "localstorage" };
  } catch {
    window.localStorage.removeItem(FALLBACK_KEY);
    return { project: null, storage: "localstorage" };
  }
}

export async function saveCurrentProject(project: PersistedProject): Promise<ProjectStorageKind> {
  if ("indexedDB" in window) {
    try {
      await saveToIndexedDb(project);
      return "indexeddb";
    } catch (caught) {
      console.warn("[P-010 project] IndexedDB save failed; using localStorage fallback", caught);
    }
  }
  window.localStorage.setItem(FALLBACK_KEY, JSON.stringify(project));
  return "localstorage";
}
